import { useRef, useState } from "react"
import { DateTime } from "luxon"
import { ButtonBase, Popover, Typography } from "@mui/material"
import { StaticDatePicker } from "@mui/x-date-pickers"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../localization/useDateTime"

const useStyles = makeStyles()((theme) => ({
    dateButton: {
        gap: ".625rem",
        height: "3rem",
        padding: ".5rem .875rem",
        borderRadius: ".75rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        background: theme.palette.common.white,
        flexShrink: 0,
        transition: "border-color 0.15s",
        "&:hover": {
            borderColor: theme.palette.tailwind.slate[400],
        },
    },
    dateButtonIcon: {
        fontSize: "1rem",
        color: theme.palette.primary.light,
        flexShrink: 0,
    },
    dateButtonText: {
        fontWeight: 700,
        color: theme.palette.tailwind.slate[800],
        whiteSpace: "nowrap",
    },
}))

type Props = Readonly<{
    selectedDate: DateTime
    onDateChange: (value: DateTime) => void
}>

export function ProductionDateButton({ selectedDate, onDateChange }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const anchorRef = useRef<HTMLButtonElement>(null)
    const [isPickerOpen, setIsPickerOpen] = useState(false)

    return (
        <>
            <ButtonBase
                ref={anchorRef}
                className={classes.dateButton}
                onClick={() => setIsPickerOpen(true)}
                aria-label={l10n.getString("meeting-service-production-view-date-button")}
            >
                <FontAwesomeIcon icon={faCalendar} className={classes.dateButtonIcon} />
                <Typography variant="body2" component="span" className={classes.dateButtonText}>
                    {dateTimeFactory.formatLongDate(selectedDate)}
                </Typography>
            </ButtonBase>

            <Popover
                open={isPickerOpen}
                anchorEl={anchorRef.current}
                onClose={() => setIsPickerOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <StaticDatePicker
                    value={selectedDate}
                    onChange={(value) => {
                        if (value) {
                            onDateChange(value)
                            setIsPickerOpen(false)
                        }
                    }}
                    onAccept={() => setIsPickerOpen(false)}
                    slotProps={{ actionBar: { actions: [] } }}
                />
            </Popover>
        </>
    )
}
